import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';

// TypeScript interfaces
interface PageTransitionProps {
  children: React.ReactNode;
  className?: string;
}

interface TransitionVariants {
  initial: {
    x: number;
    opacity: number;
    scale: number;
  };
  in: {
    x: number;
    opacity: number;
    scale: number;
    transition: {
      type: string;
      damping: number;
      stiffness: number;
      duration: number;
      ease: number[];
    };
  };
  out: {
    x: number;
    opacity: number;
    scale: number;
    transition: {
      duration: number;
      ease: number[];
    };
  };
}

const PageTransition: React.FC<PageTransitionProps> = ({ 
  children, 
  className = '' 
}) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Handle loading state for route changes
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [location.pathname]);

  // Animation variants for page transitions
  const pageVariants: TransitionVariants = {
    initial: {
      x: prefersReducedMotion ? 0 : 50, // Reduced from 100 to prevent horizontal scroll
      opacity: prefersReducedMotion ? 1 : 0,
      scale: prefersReducedMotion ? 1 : 0.95,
    },
    in: {
      x: 0, // Slide to center
      opacity: 1,
      scale: 1,
      transition: {
        type: prefersReducedMotion ? "tween" : "spring",
        damping: prefersReducedMotion ? 0 : 25,
        stiffness: prefersReducedMotion ? 0 : 400,
        duration: prefersReducedMotion ? 0.1 : 0.4,
        ease: prefersReducedMotion ? [0.25, 0.46, 0.45, 0.94] : [0.25, 0.46, 0.45, 0.94],
      }
    },
    out: {
      x: prefersReducedMotion ? 0 : -50, // Reduced from -100 to prevent horizontal scroll
      opacity: prefersReducedMotion ? 1 : 0,
      scale: prefersReducedMotion ? 1 : 0.95,
      transition: {
        duration: prefersReducedMotion ? 0.1 : 0.3,
        ease: prefersReducedMotion ? [0.25, 0.46, 0.45, 0.94] : [0.25, 0.46, 0.45, 0.94],
      }
    }
  };

  // Stagger delay for enter animation
  const staggerDelay = prefersReducedMotion ? 0 : 0.1;

  return (
    <div className="overflow-hidden">
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          variants={pageVariants}
          initial="initial"
          animate="in"
          exit="out"
          className={`min-h-screen ${className}`}
          style={{
            willChange: prefersReducedMotion ? 'auto' : 'transform, opacity',
          }}
          // Add stagger delay for enter animation
          transition={{
            ...pageVariants.in.transition,
            delay: staggerDelay,
          }}
          // Performance optimization
          layout={false}
          // Accessibility
          role="main"
          aria-live="polite"
          aria-label="Page content"
        >
          {/* Loading indicator for route changes */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-teal-500 z-50"
              style={{
                background: 'linear-gradient(90deg, #FF6B35 0%, #0891b2 100%)',
              }}
            />
          )}
          
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default PageTransition;